import { useEffect, useMemo, useState } from "react";
import styles from "../AdminPanel.module.scss";
import api from "api/axiosInstance";

type PriceRow = {
  id: number;
  title: string;
  description: string;
  price: string | null;
  direction_key: string | null;
};

type ClinicInfoRow = {
  id: number;
  name: string;
  detail: string;
};

type IconName =
  | "tooth"
  | "root"
  | "spark"
  | "crown"
  | "scalpel"
  | "implant"
  | "braces"
  | "kid";

type Direction = {
  id: string;
  title: string;
  icon?: IconName;
};

// clinic-info keys (оставляем только то, что нужно редактировать)
const KEY_DIRECTIONS = "price.directions";
const KEY_DIRECTIONS_HINT = "price.directionsHint";
const KEY_NOTE_FALLBACK = "price.note";
const KEY_BELOW_LINES = "price.belowCards.noteLines";

// defaults
const DEFAULT_HINT =
  "Стоимость указана «под ключ» (с анестезией, расходными материалами, возможной коррекцией).";

const DEFAULT_BELOW_LINES = [
  "Цены указаны для ознакомления и не являются публичной офертой.",
  "Окончательная стоимость лечения определяется после очного осмотра и диагностики.",
  "Полный прейскурант доступен в клинике.",
];

const DEFAULT_DIRECTIONS: Direction[] = [
  { id: "hygiene", title: "Профилактика\nи гигиена", icon: "tooth" },
  { id: "endo", title: "Лечение\nкорневых каналов", icon: "root" },
  { id: "esthetic", title: "Эстетика", icon: "spark" },
  { id: "prosthetics", title: "Протезирование", icon: "crown" },
  { id: "surgery", title: "Хирургия\nи пародонтология", icon: "scalpel" },
  { id: "implant", title: "Имплантация", icon: "implant" },
  { id: "ortho", title: "Ортодонтия\n(брекет-системы и элайнеры)", icon: "braces" },
  { id: "kids", title: "Детская стоматология", icon: "kid" },
];

const ICON_OPTIONS: { id: IconName; label: string }[] = [
  { id: "tooth", label: "tooth (профгигиена)" },
  { id: "root", label: "root (каналы)" },
  { id: "spark", label: "spark (эстетика)" },
  { id: "crown", label: "crown (протезирование)" },
  { id: "scalpel", label: "scalpel (хирургия)" },
  { id: "implant", label: "implant (имплантация)" },
  { id: "braces", label: "braces (ортодонтия)" },
  { id: "kid", label: "kid (дети)" },
];

function extractErr(e: any) {
  const status = e?.response?.status;
  const data = e?.response?.data;

  const msg =
    typeof data === "string"
      ? data
      : data?.message
      ? Array.isArray(data.message)
        ? data.message.join(", ")
        : String(data.message)
      : data?.error
      ? String(data.error)
      : e?.message;

  return status ? `${msg} (HTTP ${status})` : msg || "Неизвестная ошибка";
}

function parseLines(detail: string | undefined | null): string[] {
  const raw = (detail ?? "").trim();
  if (!raw) return [];

  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed
        .filter((x) => typeof x === "string")
        .map((x) => x.trim())
        .filter(Boolean);
    }
  } catch {
    // ignore
  }

  return raw
    .split(/[\n,]/g)
    .map((x) => x.trim())
    .filter(Boolean);
}

function toJsonLines(lines: string[]) {
  return JSON.stringify((lines ?? []).map((x) => x.trim()).filter(Boolean));
}

function safeParseDirections(detail: string | undefined | null): Direction[] {
  const raw = (detail ?? "").trim();
  if (!raw) return [];

  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];

    const allowedIcons = new Set<IconName>(ICON_OPTIONS.map((x) => x.id));

    const out: Direction[] = parsed
      .filter((x: any) => x && typeof x === "object")
      .map((x: any) => {
        const id = typeof x.id === "string" ? x.id.trim() : "";
        const title = typeof x.title === "string" ? x.title : "";
        const icon =
          typeof x.icon === "string" ? (x.icon.trim() as IconName) : undefined;

        return {
          id,
          title,
          icon: icon && allowedIcons.has(icon) ? icon : undefined,
        };
      })
      .filter((x) => x.id && x.title?.trim());

    const seen = new Set<string>();
    return out.filter((x) => {
      if (seen.has(x.id)) return false;
      seen.add(x.id);
      return true;
    });
  } catch {
    return [];
  }
}

function toDirectionsJson(list: Direction[]) {
  const allowedIcons = new Set<IconName>(ICON_OPTIONS.map((x) => x.id));

  const cleaned = (list ?? [])
    .map((x) => ({
      id: (x.id || "").trim(),
      title: String(x.title || ""),
      icon: x.icon && allowedIcons.has(x.icon) ? x.icon : undefined,
    }))
    .filter((x) => x.id && x.title.trim());

  const seen = new Set<string>();
  const unique = cleaned.filter((x) => {
    if (seen.has(x.id)) return false;
    seen.add(x.id);
    return true;
  });

  return JSON.stringify(unique);
}

function titleToLabel(t: string) {
  return (t || "").replaceAll("\n", " ").trim();
}

export default function PriceByDirectionsSettings() {
  // ✅ аккордеон для “Карточки направлений” (по умолчанию свернут)
  const [cardsOpen, setCardsOpen] = useState(false);

  // clinic-info rows
  const [ciRows, setCiRows] = useState<ClinicInfoRow[]>([]);
  const ciByKey = useMemo(() => {
    const m = new Map<string, ClinicInfoRow>();
    ciRows.forEach((r) => m.set(r.name, r));
    return m;
  }, [ciRows]);

  // page texts
  const [hintText, setHintText] = useState(DEFAULT_HINT);
  const [belowLinesText, setBelowLinesText] = useState(DEFAULT_BELOW_LINES.join("\n"));

  // directions cards
  const [directions, setDirections] = useState<Direction[]>(DEFAULT_DIRECTIONS);
  const [dirError, setDirError] = useState<string | null>(null);

  // prices by direction
  const [directionKey, setDirectionKey] = useState<string>("implant");
  const directionLabel = useMemo(() => {
    const found = directions.find((d) => d.id === directionKey);
    return found ? titleToLabel(found.title) : directionKey;
  }, [directionKey, directions]);

  const [rows, setRows] = useState<PriceRow[]>([]);

  // UI
  const [loadingPage, setLoadingPage] = useState(false);
  const [loadingPrices, setLoadingPrices] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);

  // form (price row)
  const [editId, setEditId] = useState<number | null>(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");

  const resetForm = () => {
    setEditId(null);
    setTitle("");
    setDescription("");
    setPrice("");
  };

  const anyLoading = loadingPage || loadingPrices;

  // load clinic-info
  const loadPageConfig = async () => {
    setLoadingPage(true);
    setErrorText(null);
    try {
      const res = await api.get<ClinicInfoRow[]>("/clinic-info");
      setCiRows(res.data ?? []);
    } catch (e: any) {
      setErrorText(`Ошибка загрузки настроек /price: ${extractErr(e)}`);
    } finally {
      setLoadingPage(false);
    }
  };

  useEffect(() => {
    loadPageConfig();
  }, []);

  useEffect(() => {
    const hint =
      (ciByKey.get(KEY_DIRECTIONS_HINT)?.detail || "").trim() ||
      (ciByKey.get(KEY_NOTE_FALLBACK)?.detail || "").trim() ||
      DEFAULT_HINT;

    const belowRaw = ciByKey.get(KEY_BELOW_LINES)?.detail;
    const belowLines = parseLines(belowRaw);

    const dirsRaw = ciByKey.get(KEY_DIRECTIONS)?.detail;
    const parsedDirs = safeParseDirections(dirsRaw);

    setHintText(hint);
    setBelowLinesText((belowLines.length ? belowLines : DEFAULT_BELOW_LINES).join("\n"));
    setDirections(parsedDirs.length ? parsedDirs : DEFAULT_DIRECTIONS);
  }, [ciByKey]);

  // keep directionKey valid when directions change
  useEffect(() => {
    if (!directions.length) return;
    if (!directions.some((d) => d.id === directionKey)) {
      setDirectionKey(directions[0].id);
      resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [directions]);

  const upsertClinicInfo = async (name: string, detail: string) => {
    const existing = ciByKey.get(name);
    if (existing) await api.delete(`/clinic-info/${existing.id}`);
    await api.post("/clinic-info", { name, detail });
  };

  const savePageTexts = async () => {
    setLoadingPage(true);
    setErrorText(null);

    try {
      const hint = (hintText || "").trim() || DEFAULT_HINT;
      const lines = parseLines(belowLinesText);
      const finalLines = lines.length ? lines : DEFAULT_BELOW_LINES;

      await upsertClinicInfo(KEY_DIRECTIONS_HINT, hint);
      await upsertClinicInfo(KEY_NOTE_FALLBACK, hint);
      await upsertClinicInfo(KEY_BELOW_LINES, toJsonLines(finalLines));

      await loadPageConfig();
    } catch (e: any) {
      setErrorText(`Ошибка сохранения текстов /price: ${extractErr(e)}`);
    } finally {
      setLoadingPage(false);
    }
  };

  const validateDirections = (list: Direction[]) => {
    const cleaned = list
      .map((d) => ({
        id: (d.id || "").trim(),
        title: String(d.title || ""),
        icon: d.icon,
      }))
      .filter((d) => d.id && d.title.trim());

    if (!cleaned.length) return { ok: false as const, msg: "Нужна хотя бы одна карточка направления." };

    const ids = cleaned.map((d) => d.id);
    if (new Set(ids).size !== ids.length) return { ok: false as const, msg: "ID направлений должны быть уникальными." };

    const bad = cleaned.find((d) => !/^[a-z0-9_-]+$/i.test(d.id));
    if (bad) {
      return {
        ok: false as const,
        msg: `Некорректный id "${bad.id}". Используй латиницу/цифры/подчёркивание/дефис.`,
      };
    }

    return { ok: true as const, cleaned: cleaned as Direction[] };
  };

  const saveDirections = async () => {
    setLoadingPage(true);
    setErrorText(null);
    setDirError(null);

    try {
      const v = validateDirections(directions);
      if (!v.ok) {
        setDirError(v.msg);
        setLoadingPage(false);
        return;
      }

      await upsertClinicInfo(KEY_DIRECTIONS, toDirectionsJson(v.cleaned));
      await loadPageConfig();
    } catch (e: any) {
      setErrorText(`Ошибка сохранения направлений: ${extractErr(e)}`);
    } finally {
      setLoadingPage(false);
    }
  };

  const resetConfigToDefault = () => {
    setHintText(DEFAULT_HINT);
    setBelowLinesText(DEFAULT_BELOW_LINES.join("\n"));
    setDirections(DEFAULT_DIRECTIONS);
    setDirError(null);
  };

  // prices
  const loadPrices = async () => {
    setLoadingPrices(true);
    setErrorText(null);
    try {
      const res = await api.get<PriceRow[]>("/price-list", { params: { directionKey } });
      setRows(res.data ?? []);
    } catch (e: any) {
      setErrorText(`Ошибка загрузки цен: ${extractErr(e)}`);
    } finally {
      setLoadingPrices(false);
    }
  };

  useEffect(() => {
    loadPrices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [directionKey]);

  const handleEdit = (r: PriceRow) => {
    setEditId(r.id);
    setTitle(r.title ?? "");
    setDescription(r.description ?? "");
    setPrice(r.price ?? "");
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Удалить строку прайса?")) return;

    setLoadingPrices(true);
    setErrorText(null);
    try {
      await api.delete(`/price-list/${id}`);
      await loadPrices();
      if (editId === id) resetForm();
    } catch (e: any) {
      setErrorText(`Ошибка удаления: ${extractErr(e)}`);
    } finally {
      setLoadingPrices(false);
    }
  };

  const handleSave = async () => {
    setLoadingPrices(true);
    setErrorText(null);

    try {
      const cleanTitle = (title || "").trim();
      const cleanDesc = (description || "").trim();
      const cleanPrice = (price || "").trim();

      if (!cleanTitle) {
        setErrorText("Укажите название услуги");
        setLoadingPrices(false);
        return;
      }

      if (!cleanDesc) {
        setErrorText("Укажите описание");
        setLoadingPrices(false);
        return;
      }

      const payload: any = {
        title: cleanTitle,
        description: cleanDesc,
        direction_key: directionKey,
      };
      if (cleanPrice) payload.price = cleanPrice;

      if (editId) await api.patch(`/price-list/${editId}`, payload);
      else await api.post("/price-list", payload);

      resetForm();
      await loadPrices();
    } catch (e: any) {
      setErrorText(`Ошибка сохранения: ${extractErr(e)}`);
    } finally {
      setLoadingPrices(false);
    }
  };

  // directions editor helpers
  const updateDirection = (idx: number, patch: Partial<Direction>) => {
    setDirections((prev) => prev.map((x, i) => (i === idx ? { ...x, ...patch } : x)));
  };

  const addDirection = () => {
    setCardsOpen(true);
    setDirections((prev) => [...prev, { id: "", title: "", icon: "tooth" }]);
  };

  const createDirectionAndGoPrices = () => {
    setCardsOpen(true);

    const id = (prompt("ID направления (латиница/цифры/дефис/подчёркивание):", "") || "").trim();
    if (!id) return;

    if (!/^[a-z0-9_-]+$/i.test(id)) {
      setDirError(`Некорректный id "${id}". Используй латиницу/цифры/подчёркивание/дефис.`);
      return;
    }

    if (directions.some((d) => d.id === id)) {
      setDirError(`ID "${id}" уже существует. Выбери другой.`);
      return;
    }

    const title = (prompt("Заголовок карточки (можно с переносом \\n):", "") || "").trim();
    if (!title) return;

    const icon = (prompt(
      "Иконка (tooth/root/spark/crown/scalpel/implant/braces/kid):",
      "tooth"
    ) || "tooth").trim() as IconName;

    const allowed = new Set<IconName>(ICON_OPTIONS.map((x) => x.id));
    const finalIcon = allowed.has(icon) ? icon : "tooth";

    setDirections((prev) => [...prev, { id, title, icon: finalIcon }]);
    setDirectionKey(id);
    resetForm();
    setDirError(null);
  };

  const moveDirection = (from: number, to: number) => {
    setDirections((prev) => {
      if (to < 0 || to >= prev.length) return prev;
      const next = [...prev];
      const [it] = next.splice(from, 1);
      next.splice(to, 0, it);
      return next;
    });
  };

  const removeDirection = async (idx: number) => {
    const d = directions[idx];
    if (!d?.id) {
      setDirections((prev) => prev.filter((_, i) => i !== idx));
      return;
    }

    let count = 0;
    try {
      const res = await api.get<PriceRow[]>("/price-list", { params: { directionKey: d.id } });
      count = (res.data ?? []).length;
    } catch {
      count = -1;
    }

    const msg =
      count >= 0
        ? `Удалить направление "${d.id}"?\n\nНайдено строк прайса по этому ключу: ${count}.\n` +
          `Если удалить карточку, строки прайса в базе останутся.\n` +
          `Рекомендуется сначала удалить/перенести цены для "${d.id}".`
        : `Удалить направление "${d.id}"?\n\nВажно: цены (price_list) для этого direction_key могут остаться в базе.`;

    if (!window.confirm(msg)) return;

    setDirections((prev) => prev.filter((_, i) => i !== idx));

    if (directionKey === d.id) {
      const next = directions.filter((_, i) => i !== idx);
      if (next.length) setDirectionKey(next[0].id);
      resetForm();
    }
  };

  return (
    <>
      <div className={styles.form_block}>
        <h3>Страница цен</h3>

        {errorText ? <div style={{ color: "#c00", marginBottom: 12 }}>{errorText}</div> : null}

        {/* ===== АККОРДЕОН: Карточки направлений ===== */}
        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button
              className={styles.btn}
              type="button"
              onClick={() => setCardsOpen((v) => !v)}
              disabled={anyLoading}
              aria-expanded={cardsOpen}
              style={{ padding: "8px 12px" }}
              title={cardsOpen ? "Свернуть" : "Развернуть"}
            >
              {cardsOpen ? "▲" : "▼"}
            </button>

            <div>
              <h3 style={{ margin: 1 }}>Карточки направлений</h3>
              <div style={{ fontSize: 13, opacity: 0.75, marginTop: 2 }}>
                Всего карточек: {directions.length}
              </div>
            </div>
          </div>

          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <button className={styles.btn_primary} type="button" onClick={addDirection} disabled={anyLoading}>
              + Добавить
            </button>
            <button className={styles.btn} type="button" onClick={createDirectionAndGoPrices} disabled={anyLoading}>
              Создать направление и перейти к ценам
            </button>
          </div>
        </div>

        {dirError ? <div style={{ color: "#c00", marginTop: 10 }}>{dirError}</div> : null}

        {cardsOpen ? (
          <>
            <div style={{ marginTop: 12, display: "grid", gap: 12 }}>
              {directions.map((d, idx) => (
                <div
                  key={idx}
                  style={{
                    border: "1px solid #e3e3e3",
                    borderRadius: 8,
                    padding: 12,
                    background: "#fff",
                  }}
                >
                  <div style={{ display: "flex", gap: 10, justifyContent: "space-between" }}>
                    <div style={{ flex: 1 }}>
                      <label>ID (латиница/цифры/дефис/подчёркивание)</label>
                      <input
                        value={d.id}
                        onChange={(e) => updateDirection(idx, { id: e.target.value })}
                        placeholder="например: implant"
                        style={{ width: "40%" }}
                      />

                      <label>Заголовок карточки (можно с переносами строк)</label>
                      <textarea
                        value={d.title}
                        onChange={(e) => updateDirection(idx, { title: e.target.value })}
                        rows={2}
                        style={{ width: "60%" }}
                        placeholder="Например: Имплантация"
                      />

                      <label>Иконка</label>
                      <select
                        value={(d.icon as any) || ""}
                        onChange={(e) =>
                          updateDirection(idx, { icon: (e.target.value as IconName) || undefined })
                        }
                        style={{ width: "40%" }}
                      >
                        {ICON_OPTIONS.map((x) => (
                          <option key={x.id} value={x.id}>
                            {x.label}
                          </option>
                        ))}
                      </select>


                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      <button
                        className={styles.btn}
                        type="button"
                        onClick={() => moveDirection(idx, idx - 1)}
                        disabled={anyLoading || idx === 0}
                      >
                        ↑
                      </button>
                      <button
                        className={styles.btn}
                        type="button"
                        onClick={() => moveDirection(idx, idx + 1)}
                        disabled={anyLoading || idx === directions.length - 1}
                      >
                        ↓
                      </button>
                      <button
                        className={styles.btn_danger}
                        type="button"
                        onClick={() => removeDirection(idx)}
                        disabled={anyLoading}
                      >
                        Удалить
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              {directions.length === 0 ? (
                <div style={{ opacity: 0.7 }}>Пока нет карточек. Нажмите «Добавить».</div>
              ) : null}
            </div>

            <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
              <button className={styles.btn_primary} onClick={saveDirections} disabled={anyLoading}>
                {loadingPage ? "Сохраняю..." : "Сохранить карточки"}
              </button>

              <button className={styles.btn} type="button" onClick={resetConfigToDefault} disabled={anyLoading}>
                Сбросить к дефолту
              </button>
            </div>
          </>
        ) : (
          <div style={{ marginTop: 10, opacity: 0.75, lineHeight: 1.5 }}>
            Блок свернут. Нажмите <b>▼</b>, чтобы развернуть редактор карточек.
          </div>
        )}

        <hr style={{ margin: "16px 0" }} />

        <label>Подсказка справа (“под ключ”)</label>
        <textarea
          value={hintText}
          onChange={(e) => setHintText(e.target.value)}
          rows={3}
          style={{ width: "60%" }}
          placeholder={DEFAULT_HINT}
        />

        <label>Дисклеймер под карточками (1 строка = 1 пункт)</label>
        <textarea
          value={belowLinesText}
          onChange={(e) => setBelowLinesText(e.target.value)}
          rows={6}
          style={{ width: "60%" }}
          placeholder={DEFAULT_BELOW_LINES.join("\n")}
        />

        <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
          <button className={styles.btn_primary} onClick={savePageTexts} disabled={anyLoading}>
            {loadingPage ? "Сохраняю..." : "Сохранить тексты"}
          </button>
        </div>
      </div>

      {/* =========================
          Цены по направлениям (price_list)
         ========================= */}
      <div className={styles.form_block}>
        <h3>Цены по направлениям</h3>

        {errorText ? <div style={{ color: "#c00", marginBottom: 12 }}>{errorText}</div> : null}

        <label>Направление</label>
        <select
          value={directionKey}
          onChange={(e) => {
            setDirectionKey(e.target.value);
            resetForm();
          }}
          style={{ width: "60%" }}
        >
          {directions.map((d) => (
            <option key={d.id} value={d.id}>
              {titleToLabel(d.title)}
            </option>
          ))}
        </select>

        <hr style={{ margin: "10px 0 16px" }} />

        <h3 style={{ marginTop: 0 }}>
          {editId ? "Редактировать строку" : "Добавить строку"} — {directionLabel}
        </h3>

        <label>Название</label>
        <input value={title} onChange={(e) => setTitle(e.target.value)} />

        <label>Описание</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          style={{ width: "60%" }}
        />

        <label>Цена (опционально)</label>
        <input value={price} onChange={(e) => setPrice(e.target.value)} placeholder="например: от 12 000 ₽" />

        <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
          <button className={styles.btn_primary} onClick={handleSave} disabled={anyLoading}>
            {loadingPrices ? "Сохраняю..." : editId ? "Сохранить" : "Создать"}
          </button>

          {editId ? (
            <button className={styles.btn} onClick={resetForm} disabled={anyLoading} type="button">
              Отмена
            </button>
          ) : null}
        </div>
      </div>

      <div className={styles.table_wrapper}>
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Название</th>
              <th>Описание</th>
              <th>Цена</th>
              <th>Действия</th>
            </tr>
          </thead>

          <tbody>
            {loadingPrices ? (
              <tr>
                <td colSpan={5} style={{ opacity: 0.7 }}>
                  Загрузка...
                </td>
              </tr>
            ) : (
              <>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td>{r.id}</td>
                    <td>{r.title || "—"}</td>
                    <td style={{ maxWidth: 520, wordBreak: "break-word" }}>{r.description || "—"}</td>
                    <td>{r.price || "—"}</td>
                    <td>
                      <div className={styles.actions}>
                        <button className={styles.btn_primary} onClick={() => handleEdit(r)} disabled={anyLoading}>
                          Редактировать
                        </button>
                        <button className={styles.btn_danger} onClick={() => handleDelete(r.id)} disabled={anyLoading}>
                          Удалить
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}

                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ opacity: 0.7 }}>
                      Пока нет строк для этого направления
                    </td>
                  </tr>
                ) : null}
              </>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}